import React, { useEffect } from 'react';
import { css } from '@compiled/react';

import { Flex, xcss, Box } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { layers } from '@atlaskit/theme/constants';
import { SpotlightTarget, SpotlightTransition, SpotlightManager } from '@atlaskit/onboarding';

import { useRightSidebarContext } from '@confluence/page-layout-context';
import { useRouteName } from '@confluence/route-manager';
import { PDF_EXPORT_PAGE, isSpaceOverview } from '@confluence/named-routes';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { PageMode } from '@confluence/page-utils/entry-points/enums';
import { AIFloatingContextMenu } from '@confluence/ai-floating-context-menu';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { fg } from '@confluence/feature-gating';
import { PageHeaderLoomEntryPoint } from '@confluence/page-header-loom-button';
import { useQuickstartEligible, useQuickstartState } from '@confluence/onboarding-quickstart';
import { useObjectSidebar } from '@confluence/object-sidebar-api';
import { useSpaceId } from '@confluence/space-utils';
import { useIsLoomDisabled } from '@confluence/loom-utils';
import { LoadableLazy } from '@confluence/loadable';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities';
import { usePreviewPanelWidth } from '@confluence/preview-panel-api';

import { OBJECT_SIDEBAR_TOUR_TARGETS } from './objectSidebarTourTargets';
import { DetailsPanelButton } from './sidebar-buttons/DetailsPanelButton';
import { WorkflowsPanelButton } from './sidebar-buttons/WorkflowsPanelButton';
import { ObjectSidebarReactionsPicker } from './sidebar-buttons/ObjectSidebarReactionsPicker';
import { CommentPanelButton } from './sidebar-buttons/CommentPanelButton';

const ObjectSidebarSpotlightTour = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ObjectSidebarSpotlightTour" */ './ObjectSidebarSpotlightTour'
			)
		).ObjectSidebarSpotlightTour,
});

const objectSidebarStyles = css({
	position: 'fixed',
	right: 0,
	bottom: 0,
	paddingRight: `${token('space.300')}`,
	paddingBottom: `${token('space.100')}`,
	zIndex: layers.dialog(),
	pointerEvents: 'none',
	// The selector below ensures the elements behind the empty space taken up by the container remain clickable/responsive to mouse events
	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
	'& > *': {
		pointerEvents: 'auto',
	},
});

const viewModeStyles = css({
	//z index is set to 1 to ensure that the object sidebar is below the inline comment component
	zIndex: 1,
});

const quickstartSpacing = css({
	marginBottom: `${token('space.1000')}`,
	right: `${token('space.300')}`,
});

const quickstartLivePageSpacing = css({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginBottom: '110px',
	right: `${token('space.300')}`,
});

const objectSidebarContainerStyles = xcss({
	padding: 'space.100',
	boxShadow: 'elevation.shadow.raised',
	backgroundColor: 'elevation.surface.raised',
	borderRadius: '64px',
	width: '48px',
	marginLeft: 'auto',
});

const dividerStyle = xcss({
	borderBottom: `2px solid ${token('color.border')}`,
	display: 'inline-block',
	width: '16px',
	height: '1px',
});

const bottomSidebarStyles = xcss({
	marginTop: 'space.100',
});

type ObjectSidebarControlProps = {
	hasCommentsButton?: boolean;
	hasDetailsButton?: boolean;
	hasLoomButton?: boolean;
	hasAiButton?: boolean;
	contentId: string;
	spaceKey?: string;
	contentType?: string | null;
	lastModifiedDate?: string | null;
	pageCommentsSectionInView?: boolean;
};

export const ObjectSidebarControl = ({
	hasCommentsButton,
	hasDetailsButton,
	hasLoomButton,
	hasAiButton,
	contentId,
	spaceKey,
	contentType,
	lastModifiedDate,
	pageCommentsSectionInView,
}: ObjectSidebarControlProps) => {
	const [{ sidebarControlState }, { setSidebarControlState }] = useObjectSidebar();

	const pageMode = useGetPageMode();
	const spaceId = useSpaceId();
	const isLoomDisabled = useIsLoomDisabled();
	const isSpaceOverviewPage = isSpaceOverview();

	const rightSidebarContext = useRightSidebarContext();
	const { width: globalSidebarWidth } = rightSidebarContext.getCurrent() || {};
	const { width: previewPanelWidth } = usePreviewPanelWidth();

	const isOnPDFExportPageRouteArgs = {
		selector: (routeName: string | undefined) => {
			if (!routeName) {
				return false;
			}
			return routeName === PDF_EXPORT_PAGE.name;
		},
	};

	const isOnPDFExportPageRoute = useRouteName(isOnPDFExportPageRouteArgs);

	const isLivePage = useIsCurrentPageLive();
	const renderServerPlaceholder = useRenderServerPlaceholder();

	const { isDismissed } = useQuickstartState();
	const { isQuickstartEligible } = useQuickstartEligible();
	const isQuickStartPillShowing = !isDismissed && isQuickstartEligible();

	useEffect(() => {
		// This check can be removed when we are rendering the object sidebar
		// control on server as well so server and client have same output and
		// hydrateRoot doesn't break
		if (!renderServerPlaceholder) {
			setSidebarControlState('shown');
		}

		return () => setSidebarControlState('hidden');
	}, [setSidebarControlState, renderServerPlaceholder]);

	const primaryActions: JSX.Element[] = [];
	if (hasCommentsButton && fg('confluence-frontend-comments-panel')) {
		primaryActions.push(<CommentPanelButton contentId={contentId} />);
	}
	if (hasDetailsButton && fg('confluence_frontend_details_panel')) {
		primaryActions.push(<DetailsPanelButton contentId={contentId} />);
	}
	if (fg('document_workflows')) {
		primaryActions.push(<WorkflowsPanelButton />);
	}

	const secondaryActions: JSX.Element[] = [];

	if (
		!(isSpaceOverviewPage || pageMode === PageMode.EDIT) &&
		fg('confluence_new_reactions_entry_point_changes')
	) {
		secondaryActions.push(
			<ObjectSidebarReactionsPicker
				contentId={contentId}
				spaceId={spaceId ?? ''}
				contentSubType={pageMode}
				contentType={contentType ?? ''}
			/>,
		);
	}

	if (hasLoomButton && !isLoomDisabled && fg('confluence_frontend_object_sidebar_loom')) {
		secondaryActions.push(
			<PageHeaderLoomEntryPoint
				contentId={contentId}
				crossSellPopupPlacement="left-end"
				spaceKey={spaceKey ?? ''}
				contentType={contentType ?? ''}
				isCircle
				testId="sidebar-loom-button"
				source="contentWrapper"
			/>,
		);
	}

	if (isOnPDFExportPageRoute) {
		return null;
	}

	const globalSidebarMargin =
		pageMode === PageMode.VIEW && globalSidebarWidth ? globalSidebarWidth : 0;
	const previewPanelMargin = previewPanelWidth ?? 0;

	return sidebarControlState === 'shown' ||
		(renderServerPlaceholder && fg('confluence_frontend_object_sidebar_ssr')) ? (
		<div
			css={[
				objectSidebarStyles,
				pageMode === PageMode.VIEW && viewModeStyles,
				isQuickStartPillShowing && !isLivePage && quickstartSpacing,
				isQuickStartPillShowing && isLivePage && quickstartLivePageSpacing,
			]}
			data-testid="object-sidebar-container"
			data-vc="object-sidebar-container"
			style={{
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
				marginRight: globalSidebarMargin + previewPanelMargin,
			}}
		>
			{primaryActions.length + secondaryActions.length > 0 &&
				fg('confluence_frontend_object_sidebar') && (
					<SpotlightManager>
						<SpotlightTarget name={OBJECT_SIDEBAR_TOUR_TARGETS.OBJECT_SIDEBAR_CONTROL}>
							<Flex
								xcss={objectSidebarContainerStyles}
								direction="column"
								gap="space.100"
								justifyContent="center"
								alignItems="center"
								testId="object-sidebar-control"
							>
								{primaryActions}
								{secondaryActions.length > 0 && <Box xcss={dividerStyle} />}
								{secondaryActions}
							</Flex>
						</SpotlightTarget>
						<SpotlightTransition>
							<ObjectSidebarSpotlightTour />
						</SpotlightTransition>
					</SpotlightManager>
				)}
			{hasAiButton && (
				<Box xcss={bottomSidebarStyles}>
					<AIFloatingContextMenu
						spaceKey={spaceKey ?? ''}
						contentId={contentId}
						contentType={contentType ?? ''}
						lastModifiedDate={lastModifiedDate}
						pageCommentsSectionInView={pageCommentsSectionInView}
						isLivePage={isLivePage}
						isInObjectSideBar
					/>
				</Box>
			)}
		</div>
	) : null;
};
